import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { useThreeDContext, isWebGLAvailable } from '../../store/ThreeDContext';
import { VdpTemplate, CSVData } from '../../types';
import { 
  Box, 
  RotateCw, 
  RotateCcw, 
  Sun, 
  Maximize2, 
  X, 
  Sparkles, 
  Droplets, 
  Eye, 
  Layers, 
  Focus,
  RefreshCw,
  Camera,
  Compass
} from 'lucide-react';
import { 
  renderTemplateMultiMapToCanvases, 
  projectCanvasToUvMap,
  createProportionalCanvas
} from '../../utils/elementMaterialRenderer';

interface Studio3DSplitViewerProps {
  template: VdpTemplate;
  csvData: CSVData | null;
  previewRecordIndex?: number;
  onOpenFullScreenModal: () => void;
  onClose: () => void;
}

export const Studio3DSplitViewer: React.FC<Studio3DSplitViewerProps> = ({
  template,
  csvData,
  previewRecordIndex = 0,
  onOpenFullScreenModal,
  onClose
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const threeD = useThreeDContext();
  
  const [isRotating, setIsRotating] = useState(true);
  const [lightingPreset, setLightingPreset] = useState<'studio' | 'showroom' | 'warm'>('studio');
  const [showCondensation, setShowCondensation] = useState(false);
  const [webGlSupported, setWebGlSupported] = useState(true);

  // Three.js instances ref
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const containerMeshRef = useRef<THREE.Mesh | null>(null);
  const capMeshRef = useRef<THREE.Mesh | null>(null);
  const labelMeshRef = useRef<THREE.Mesh | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);
  const prevMousePos = useRef({ x: 0, y: 0 });
  const labelTextureRef = useRef<THREE.CanvasTexture | null>(null);
  const uvCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // Check WebGL availability
  useEffect(() => {
    setWebGlSupported(isWebGLAvailable());
  }, []);

  // Initialize Three.js Scene
  useEffect(() => {
    if (!containerRef.current || !webGlSupported) return;

    const width = containerRef.current.clientWidth || 400;
    const height = containerRef.current.clientHeight || 500;

    // 1. Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(lightingPreset === 'warm' ? 0x18100c : 0x090d16);
    sceneRef.current = scene;

    // 2. Camera
    const camera = new THREE.PerspectiveCamera(42, width / height, 0.1, 100);
    camera.position.set(0, 0.4, 4.5);
    cameraRef.current = camera;

    // 3. Renderer with antialiasing
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.15;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    rendererRef.current = renderer;

    // Room Environment for realistic PBR reflections
    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    scene.environment = pmremGenerator.fromScene(new RoomEnvironment()).texture;

    // Attach to DOM
    containerRef.current.innerHTML = '';
    containerRef.current.appendChild(renderer.domElement);

    // 4. Studio Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.85);
    scene.add(ambientLight);

    const keyLight = new THREE.DirectionalLight(0xffffff, 1.8);
    keyLight.position.set(3, 4, 4);
    keyLight.castShadow = true;
    scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(0x90cdf4, 1.0);
    fillLight.position.set(-3, 2, -2);
    scene.add(fillLight);

    const rimLight = new THREE.DirectionalLight(0xa855f7, 1.2);
    rimLight.position.set(0, -3, -3);
    scene.add(rimLight);

    // Floor shadow catcher
    const floorGeo = new THREE.PlaneGeometry(12, 12);
    const floorMat = new THREE.ShadowMaterial({ opacity: 0.35 });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -1.5;
    floor.receiveShadow = true;
    scene.add(floor);

    // Resize Handler
    const handleResize = () => {
      if (!containerRef.current || !rendererRef.current || !cameraRef.current) return;
      const w = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;
      cameraRef.current.aspect = w / h;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(w, h);
    };

    window.addEventListener('resize', handleResize);

    // Animation Loop
    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);

      if (isRotating && containerMeshRef.current) {
        containerMeshRef.current.rotation.y += 0.008;
      }

      renderer.render(scene, camera);
    };
    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      pmremGenerator.dispose();
      renderer.dispose();
    };
  }, [webGlSupported, lightingPreset]);

  // Re-build 3D Model Mesh when selected model changes
  useEffect(() => {
    if (!sceneRef.current) return;
    const scene = sceneRef.current;

    // Remove old meshes
    if (containerMeshRef.current) {
      scene.remove(containerMeshRef.current);
      containerMeshRef.current = null;
    }

    const modelType = threeD.selectedModel || 'bottle';
    const group = new THREE.Group();

    // Material finish config
    const finish = threeD.selectedFinish || 'gloss';
    let labelRoughness = 0.1;
    let labelMetalness = 0.0;
    if (finish === 'matte') { labelRoughness = 0.85; labelMetalness = 0.0; }
    else if (finish === 'gold_foil') { labelRoughness = 0.08; labelMetalness = 0.98; }
    else if (finish === 'silver_foil') { labelRoughness = 0.10; labelMetalness = 0.96; }
    else if (finish === 'holographic') { labelRoughness = 0.06; labelMetalness = 0.98; }
    else if (finish === 'kraft') { labelRoughness = 0.9; labelMetalness = 0.0; }

    // Label texture & material
    const labelMat = new THREE.MeshPhysicalMaterial({
      roughness: labelRoughness,
      metalness: labelMetalness,
      clearcoat: finish === 'gloss' ? 1.0 : 0.0,
      clearcoatRoughness: 0.1,
      side: THREE.DoubleSide
    });

    if (labelTextureRef.current) {
      labelMat.map = labelTextureRef.current;
      labelMat.needsUpdate = true;
    }

    // Geometry generators based on model type
    if (modelType === 'bottle') {
      // Glass Beverage Bottle
      const bodyMat = new THREE.MeshPhysicalMaterial({
        color: 0xffffff,
        roughness: 0.05,
        transmission: 0.95,
        opacity: 1,
        transparent: true,
        ior: 1.5,
        thickness: 0.6
      });

      const bodyGeo = new THREE.CylinderGeometry(0.72, 0.75, 2.2, 48);
      const bodyMesh = new THREE.Mesh(bodyGeo, bodyMat);
      bodyMesh.castShadow = true;
      group.add(bodyMesh);

      // Shoulder & Neck
      const neckGeo = new THREE.CylinderGeometry(0.24, 0.72, 1.0, 48);
      const neckMesh = new THREE.Mesh(neckGeo, bodyMat);
      neckMesh.position.y = 1.6;
      group.add(neckMesh);

      // Metal Cap
      const capGeo = new THREE.CylinderGeometry(0.26, 0.26, 0.3, 32);
      const capMat = new THREE.MeshStandardMaterial({ color: 0x1e293b, metalness: 0.9, roughness: 0.2 });
      const capMesh = new THREE.Mesh(capGeo, capMat);
      capMesh.position.y = 2.25;
      group.add(capMesh);

      // Label Mesh (wraps around body)
      const labelGeo = new THREE.CylinderGeometry(0.73, 0.76, 1.45, 48, 1, true, -Math.PI * 0.45, Math.PI * 0.9);
      const labelMesh = new THREE.Mesh(labelGeo, labelMat);
      labelMesh.position.y = 0;
      labelMeshRef.current = labelMesh;
      group.add(labelMesh);

    } else if (modelType === 'can') {
      // Aluminum Soda / Beer Can
      const canMat = new THREE.MeshStandardMaterial({
        color: 0xd8e2ec,
        metalness: 0.92,
        roughness: 0.22
      });

      const canGeo = new THREE.CylinderGeometry(0.78, 0.78, 2.6, 48);
      const canMesh = new THREE.Mesh(canGeo, canMat);
      canMesh.castShadow = true;
      group.add(canMesh);

      // Can Rim Top & Bottom
      const rimTopGeo = new THREE.TorusGeometry(0.74, 0.05, 16, 48);
      const rimMat = new THREE.MeshStandardMaterial({ color: 0xcfd8dc, metalness: 0.98, roughness: 0.1 });
      const rimTopMesh = new THREE.Mesh(rimTopGeo, rimMat);
      rimTopMesh.rotation.x = Math.PI / 2;
      rimTopMesh.position.y = 1.3;
      group.add(rimTopMesh);

      // Label Mesh wrapped around can
      const labelGeo = new THREE.CylinderGeometry(0.785, 0.785, 2.1, 48, 1, true, -Math.PI * 0.55, Math.PI * 1.1);
      const labelMesh = new THREE.Mesh(labelGeo, labelMat);
      labelMesh.position.y = 0;
      labelMeshRef.current = labelMesh;
      group.add(labelMesh);

    } else if (modelType === 'pouch') {
      // Stand-Up Foil Pouch
      const pouchMat = new THREE.MeshStandardMaterial({
        color: 0x1e293b,
        roughness: 0.45,
        metalness: 0.35
      });

      const pouchGeo = new THREE.BoxGeometry(1.6, 2.4, 0.65);
      const pouchMesh = new THREE.Mesh(pouchGeo, pouchMat);
      pouchMesh.castShadow = true;
      group.add(pouchMesh);

      // Pouch Zipper Top Seal
      const sealGeo = new THREE.BoxGeometry(1.65, 0.2, 0.2);
      const sealMat = new THREE.MeshStandardMaterial({ color: 0xf59e0b, metalness: 0.7, roughness: 0.3 });
      const sealMesh = new THREE.Mesh(sealGeo, sealMat);
      sealMesh.position.y = 1.3;
      group.add(sealMesh);

      // Label Mesh on front face
      const labelGeo = new THREE.PlaneGeometry(1.4, 1.8);
      const labelMesh = new THREE.Mesh(labelGeo, labelMat);
      labelMesh.position.set(0, -0.1, 0.33);
      labelMeshRef.current = labelMesh;
      group.add(labelMesh);

    } else if (modelType === 'box') {
      // Product Shipping / Luxury Retail Box
      const boxMat = new THREE.MeshStandardMaterial({
        color: 0x27272a,
        roughness: 0.7,
        metalness: 0.05
      });

      const boxGeo = new THREE.BoxGeometry(1.8, 2.2, 1.2);
      const boxMesh = new THREE.Mesh(boxGeo, boxMat);
      boxMesh.castShadow = true;
      group.add(boxMesh);

      // Front Label
      const labelGeo = new THREE.PlaneGeometry(1.6, 1.8);
      const labelMesh = new THREE.Mesh(labelGeo, labelMat);
      labelMesh.position.set(0, 0, 0.605);
      labelMeshRef.current = labelMesh;
      group.add(labelMesh);

    } else if (modelType === 'jar') {
      // Cosmetic Glass Jar
      const jarMat = new THREE.MeshPhysicalMaterial({
        color: 0xffffff,
        roughness: 0.15,
        transmission: 0.9,
        transparent: true,
        thickness: 0.8
      });

      const jarGeo = new THREE.CylinderGeometry(0.95, 0.95, 1.2, 48);
      const jarMesh = new THREE.Mesh(jarGeo, jarMat);
      jarMesh.castShadow = true;
      group.add(jarMesh);

      // Jar Lid
      const lidGeo = new THREE.CylinderGeometry(0.98, 0.98, 0.35, 48);
      const lidMat = new THREE.MeshStandardMaterial({ color: 0x111827, metalness: 0.9, roughness: 0.15 });
      const lidMesh = new THREE.Mesh(lidGeo, lidMat);
      lidMesh.position.y = 0.75;
      group.add(lidMesh);

      // Label wrapped around jar body
      const labelGeo = new THREE.CylinderGeometry(0.96, 0.96, 0.8, 48, 1, true, -Math.PI * 0.45, Math.PI * 0.9);
      const labelMesh = new THREE.Mesh(labelGeo, labelMat);
      labelMesh.position.y = -0.05;
      labelMeshRef.current = labelMesh;
      group.add(labelMesh);

    } else {
      // Coffee Cup / Tumbler
      const cupMat = new THREE.MeshStandardMaterial({
        color: 0xf1f5f9,
        roughness: 0.35,
        metalness: 0.1
      });

      const cupGeo = new THREE.CylinderGeometry(0.85, 0.65, 2.3, 48);
      const cupMesh = new THREE.Mesh(cupGeo, cupMat);
      cupMesh.castShadow = true;
      group.add(cupMesh);

      // Cup Lid
      const lidGeo = new THREE.CylinderGeometry(0.9, 0.88, 0.3, 48);
      const lidMat = new THREE.MeshStandardMaterial({ color: 0x0f172a, roughness: 0.6 });
      const lidMesh = new THREE.Mesh(lidGeo, lidMat);
      lidMesh.position.y = 1.25;
      group.add(lidMesh);

      // Label Mesh
      const labelGeo = new THREE.CylinderGeometry(0.86, 0.72, 1.4, 48, 1, true, -Math.PI * 0.5, Math.PI);
      const labelMesh = new THREE.Mesh(labelGeo, labelMat);
      labelMesh.position.y = 0.1;
      labelMeshRef.current = labelMesh;
      group.add(labelMesh);
    }

    group.position.y = -0.2;
    scene.add(group);
    containerMeshRef.current = group as any;

  }, [threeD.selectedModel, threeD.selectedFinish]);

  // Real-Time Canvas Texture Generator from current 2D template state
  useEffect(() => {
    let isCancelled = false;

    const generateTexture = async () => {
      try {
        const tW = template?.width || 100;
        const tH = template?.height || 100;

        const labelCanvas = createProportionalCanvas(tW, tH, 1024);
        const roughnessCanvas = createProportionalCanvas(tW, tH, 1024);
        const metalnessCanvas = createProportionalCanvas(tW, tH, 1024);
        const bumpCanvas = createProportionalCanvas(tW, tH, 1024);

        // Render template multi-maps with current variable record
        await renderTemplateMultiMapToCanvases({
          labelCanvas,
          roughnessCanvas,
          metalnessCanvas,
          bumpCanvas,
          template,
          csvData,
          elementMaterials: threeD.elementMaterials,
          globalFinish: threeD.selectedFinish || 'gloss',
          globalEmbossMode: threeD.embossMode || 'none',
          globalReliefHeight: threeD.reliefHeight || 1.2
        });

        if (isCancelled) return;

        // Dispose previous texture to avoid GPU memory leaks
        if (labelTextureRef.current) {
          labelTextureRef.current.dispose();
        }

        const texture = new THREE.CanvasTexture(labelCanvas);
        texture.anisotropy = 4;
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.needsUpdate = true;
        labelTextureRef.current = texture;

        const roughnessTexture = new THREE.CanvasTexture(roughnessCanvas);
        roughnessTexture.needsUpdate = true;

        const metalnessTexture = new THREE.CanvasTexture(metalnessCanvas);
        metalnessTexture.needsUpdate = true;

        const bumpTexture = new THREE.CanvasTexture(bumpCanvas);
        bumpTexture.needsUpdate = true;

        if (labelMeshRef.current && labelMeshRef.current.material) {
          const mat = labelMeshRef.current.material as THREE.MeshPhysicalMaterial;
          mat.map = texture;
          mat.roughnessMap = roughnessTexture;
          mat.metalnessMap = metalnessTexture;
          mat.bumpMap = bumpTexture;
          mat.bumpScale = 0.05;
          mat.needsUpdate = true;
        }
      } catch (err) {
        console.warn('Split 3D Texture Generation Warning:', err);
      }
    };

    // Debounce to allow smooth 60fps drag on 2D canvas without lagging
    const timeout = setTimeout(generateTexture, 80);

    return () => {
      isCancelled = true;
      clearTimeout(timeout);
    };
  }, [template, csvData, previewRecordIndex, threeD.selectedFinish, threeD.selectedModel, threeD.elementMaterials]);

  // Pointer Drag to Orbit 3D Container
  const handlePointerDown = (e: React.PointerEvent) => {
    isDraggingRef.current = true;
    prevMousePos.current = { x: e.clientX, y: e.clientY };
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDraggingRef.current || !containerMeshRef.current) return;
    const deltaX = e.clientX - prevMousePos.current.x;
    const deltaY = e.clientY - prevMousePos.current.y;
    prevMousePos.current = { x: e.clientX, y: e.clientY };

    containerMeshRef.current.rotation.y += deltaX * 0.012;
    containerMeshRef.current.rotation.x = Math.max(-0.6, Math.min(0.6, containerMeshRef.current.rotation.x + deltaY * 0.008));
  };

  const handlePointerUp = () => {
    isDraggingRef.current = false;
  };

  const resetCamera = () => {
    if (cameraRef.current && containerMeshRef.current) {
      cameraRef.current.position.set(0, 0.4, 4.5);
      containerMeshRef.current.rotation.set(0, 0, 0);
    }
  };

  return (
    <div className="flex flex-col h-full bg-zinc-950 border-l border-zinc-800 relative select-none overflow-hidden">
      {/* 3D Header Toolbar */}
      <div className="h-10 px-3 border-b border-zinc-800 bg-zinc-900/90 backdrop-blur-md flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <Box className="w-4 h-4 text-blue-400" />
          <span className="text-xs font-bold text-zinc-100 uppercase tracking-wider">
            Live 3D Packaging Mockup
          </span>
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 font-mono border border-blue-500/30">
            Real-Time PBR
          </span>
        </div>

        <div className="flex items-center gap-1">
          {/* Auto Rotate Toggle */}
          <button
            onClick={() => setIsRotating(!isRotating)}
            className={`p-1.5 rounded-lg text-xs transition-colors cursor-pointer ${
              isRotating ? 'bg-blue-600/30 text-blue-400 border border-blue-500/40' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
            title={isRotating ? 'Pause 360° Rotation' : 'Start 360° Rotation'}
          >
            <RotateCw className={`w-3.5 h-3.5 ${isRotating ? 'animate-spin' : ''}`} />
          </button>

          {/* Reset Camera */}
          <button
            onClick={resetCamera}
            className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors cursor-pointer"
            title="Reset Camera View"
          >
            <Focus className="w-3.5 h-3.5" />
          </button>

          {/* Fullscreen Modal Toggle */}
          <button
            onClick={onOpenFullScreenModal}
            className="p-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs transition-colors cursor-pointer flex items-center gap-1 shadow-xs"
            title="Open Full 3D Packaging Proof Studio"
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>

          {/* Close Split View */}
          <button
            onClick={onClose}
            className="p-1.5 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-lg transition-colors cursor-pointer ml-1"
            title="Close 3D Split View"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Model & Material Quick Selector */}
      <div className="p-2 border-b border-zinc-800/80 bg-zinc-950/80 flex items-center gap-2 overflow-x-auto custom-scrollbar shrink-0 text-xs">
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-[10px] uppercase font-bold text-zinc-500">Model:</span>
          <select
            value={threeD.selectedModel || 'bottle'}
            onChange={(e) => threeD.update3DState({ selectedModel: e.target.value })}
            className="bg-zinc-900 border border-zinc-700 text-zinc-200 text-xs px-2 py-1 rounded-md outline-none focus:border-blue-500 font-medium cursor-pointer"
          >
            <option value="bottle">🍾 Glass Bottle</option>
            <option value="can">🥫 Aluminum Can</option>
            <option value="pouch">👝 Foil Pouch</option>
            <option value="box">📦 Product Box</option>
            <option value="jar">🫙 Glass Jar</option>
            <option value="cup">☕ Tumbler / Cup</option>
          </select>
        </div>

        <div className="flex items-center gap-1.5 shrink-0 ml-1">
          <span className="text-[10px] uppercase font-bold text-zinc-500">Finish:</span>
          <select
            value={threeD.selectedFinish || 'gloss'}
            onChange={(e) => threeD.update3DState({ selectedFinish: e.target.value })}
            className="bg-zinc-900 border border-zinc-700 text-zinc-200 text-xs px-2 py-1 rounded-md outline-none focus:border-blue-500 font-medium cursor-pointer"
          >
            <option value="gloss">✨ Gloss Varnish</option>
            <option value="matte">🪨 Velvet Matte</option>
            <option value="gold_foil">🌟 24K Gold Foil</option>
            <option value="silver_foil">🪙 Chrome Silver</option>
            <option value="kraft">🪵 Kraft Paper</option>
          </select>
        </div>
      </div>

      {/* Interactive 3D WebGL Canvas Viewport */}
      <div 
        ref={containerRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        className="flex-1 w-full relative cursor-grab active:cursor-grabbing overflow-hidden min-h-0"
      >
        {!webGlSupported && (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center text-zinc-400">
            <Box className="w-10 h-10 text-zinc-600 mb-2" />
            <p className="text-sm font-semibold text-zinc-300">WebGL hardware acceleration disabled</p>
            <p className="text-xs text-zinc-500 mt-1">Please enable WebGL in your browser settings to view live 3D mockups.</p>
          </div>
        )}

        {/* Orbit Drag Helper Overlay */}
        <div className="absolute bottom-2 left-2 px-2 py-1 bg-zinc-950/80 backdrop-blur-xs border border-zinc-800 rounded text-[10px] text-zinc-400 pointer-events-none font-mono flex items-center gap-1.5">
          <Compass className="w-3 h-3 text-blue-400" />
          <span>Click &amp; drag to orbit 3D model</span>
        </div>
      </div>
    </div>
  );
};
